/**
 * Chart Studio → field-detail hover card, as pure data.
 * Figma: `z2UTps4F1dKNh1bbD4LtYI` header `6433:560612`, body `6433:560623`.
 *
 * Same contract as the other registries: copy and layout live here, never JSX
 * or callbacks. Renaming a chip, rewording the summary or reordering the stat
 * tiles is an edit to this file only — the mapper and the card follow.
 *
 * Templates use `{token}` placeholders, filled by the mapper:
 *   {name}       the field's display label
 *   {unique}     the formatted unique count
 *   {distinct}   `distinctNoun.one` / `.other`, by the unique count
 *   {mostCommon} the most common value (only in `mostCommon` clauses)
 */
import type { FieldDetailStatId } from "../../services/chartStudio/chartStudioDataModel.types";
import type { ModelFieldKindApi } from "../../services/storyCanvas/storyCanvas.types";

export interface FieldDetailDescriptionTemplate {
  summary: string;
  /** Appended after `summary`; dropped when the API has no most common value. */
  mostCommon: string;
}

export interface FieldDetailStatTileConfig {
  id: FieldDetailStatId;
  label: string;
  /** Columns the tile takes in the card's 2-column grid. */
  span: 1 | 2;
}

export interface FieldDetailCardConfig {
  /**
   * Second chip: the field's analytical role, from the detail's `kind`. Both
   * dimension kinds read "Dimension": to a chart author a degenerate
   * dimension is a dimension kept on the fact table, not a different role.
   */
  roleLabels: Record<ModelFieldKindApi, string>;
  descriptions: Record<ModelFieldKindApi, FieldDetailDescriptionTemplate>;
  /** Used when the backend sends a `kind` this build doesn't know yet. */
  fallbackDescription: FieldDetailDescriptionTemplate;
  distinctNoun: { one: string; other: string };
  completenessLabel: string;
  /** Shown in a tile whose value the API returned as null / empty. */
  emptyValue: string;
  /** Registry order is the tile order (Figma: Total, Unique, then Most common full width). */
  stats: readonly FieldDetailStatTileConfig[];
  /** Body state copy. Figma draws only the loaded card, so these are product copy. */
  states: { loading: string; retryLabel: string };
}

const MOST_COMMON_CLAUSE = " Most common: “{mostCommon}”";

export const fieldDetailCard = {
  roleLabels: {
    measure: "Measure",
    dimension_attribute: "Dimension",
    degenerate_dimension: "Dimension",
  },
  descriptions: {
    measure: {
      summary: "Numeric field “{name}” with {unique} {distinct}.",
      mostCommon: MOST_COMMON_CLAUSE,
    },
    dimension_attribute: {
      summary: "Categorical field “{name}” with {unique} {distinct}.",
      mostCommon: MOST_COMMON_CLAUSE,
    },
    degenerate_dimension: {
      summary: "Categorical field “{name}” with {unique} {distinct}.",
      mostCommon: MOST_COMMON_CLAUSE,
    },
  },
  fallbackDescription: {
    summary: "Field “{name}” with {unique} {distinct}.",
    mostCommon: MOST_COMMON_CLAUSE,
  },
  distinctNoun: { one: "distinct value", other: "distinct values" },
  completenessLabel: "Completeness",
  states: {
    loading: "Loading field details…",
    retryLabel: "Retry",
  },
  emptyValue: "—",
  stats: [
    { id: "total", label: "Total", span: 1 },
    { id: "unique", label: "Unique", span: 1 },
    { id: "mostCommon", label: "Most common", span: 2 },
  ],
} as const satisfies FieldDetailCardConfig;
